import { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import { CheckCircle2, ArrowRight, Loader2, Shield } from 'lucide-react';
import apiV2 from '../utils/apiV2';
import { toast } from 'sonner';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import ProcessorCards from './payments/ProcessorCards';
import { UnifiedLedger } from './payments/UnifiedLedger';
import { SubscriptionsPanel } from './payments/SubscriptionsPanel';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

/**
 * Payments — Stripe and Ziina as side-by-side processor cards up top (each
 * owns its own connection state + actions), then ONE unified transactions
 * ledger across all processors.
 */
const PaymentsTabSimple = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [ziinaStatus, setZiinaStatus] = useState(null);
  const [ziinaLoaded, setZiinaLoaded] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showTrustModal, setShowTrustModal] = useState(false);
  const [ledgerSummary, setLedgerSummary] = useState(null);

  const orgId = localStorage.getItem('organizationId');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeSuccess = urlParams.get('stripe_success');
    const stripeRefresh = urlParams.get('stripe_refresh');
    const stripeError = urlParams.get('stripe_error');
    const errorMessage = urlParams.get('message');

    if (stripeSuccess) {
      toast.success('Stripe account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      window.dispatchEvent(new Event('setupDataChanged'));
      checkStatus(true);
    } else if (stripeRefresh) {
      window.history.replaceState({}, '', window.location.pathname);
      checkStatus(true);
    } else if (stripeError) {
      toast.error(`Stripe connection failed: ${errorMessage || stripeError}`);
      window.history.replaceState({}, '', window.location.pathname);
      checkStatus();
    } else {
      checkStatus();
    }
    // ziina_oauth return toasts are handled centrally in DashboardV2 (the tab
    // deep-link effect clears query params before this component mounts).
    loadZiina();
  }, []); // eslint-disable-line

  const checkStatus = async (forceRefresh = false) => {
    if (!orgId) { setLoading(false); return; }
    try {
      const response = await apiV2.get(`/organizations/${orgId}/stripe-connect/status`, {
        params: { refresh: forceRefresh },
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadZiina = useCallback(async () => {
    if (!orgId) { setZiinaLoaded(true); return; }
    try {
      const r = await apiV2.get(`/organizations/${orgId}/ziina/status`);
      setZiinaStatus(r.data);
    } catch {
      setZiinaStatus({ eligible: false, connected: false, provider: 'stripe' });
    } finally {
      setZiinaLoaded(true);
    }
  }, [orgId]);

  const handleConnect = () => setShowTrustModal(true);

  const handleConfirmStripe = async () => {
    setShowTrustModal(false);
    setConnecting(true);
    try {
      const response = await apiV2.get(`/organizations/${orgId}/stripe-connect/oauth-url`);
      const { url } = response.data;
      if (!url) throw new Error('Failed to generate Stripe connection URL');
      window.location.href = url;
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to connect to Stripe'));
      setConnecting(false);
    }
  };

  const switchProvider = async (provider) => {
    try {
      const r = await apiV2.post(`/organizations/${orgId}/ziina/provider`, { provider });
      setZiinaStatus(r.data);
      toast.success(`Payments now run on ${provider === 'ziina' ? 'Ziina' : 'Stripe'}`);
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not switch processor'));
    }
  };

  if (loading || !ziinaLoaded) {
    return (
      <TabPageLayout title="Payments" subtitle="Manage your payment processing">
        <PageLoader />
      </TabPageLayout>
    );
  }

  const stripeConnected = !!status?.onboardingComplete;
  const ziinaConnected = !!ziinaStatus?.connected;
  const provider = ziinaStatus?.provider || 'stripe';
  const ziinaActive = provider === 'ziina' && ziinaConnected;
  const stripeActive = provider === 'stripe' && stripeConnected;

  const subtitle = ziinaActive
    ? (ziinaStatus?.oauthConnected
        ? 'Payments land in your Ziina wallet — BookingFlows 3% auto-collected'
        : 'Payments collected by BookingFlows, paid out to your Ziina wallet')
    : stripeActive
      ? 'Payments run on your Stripe account'
      : 'Set up payment processing for your bookings';

  return (
    <TabPageLayout title="Payments" subtitle={subtitle}>
      <div className="max-w-4xl space-y-3">
        <ProcessorCards
          orgId={orgId}
          provider={provider}
          stripeStatus={status}
          ziinaStatus={ziinaStatus}
          onZiinaStatus={setZiinaStatus}
          onSwitch={switchProvider}
          onConnectStripe={handleConnect}
          connectingStripe={connecting}
          onRefreshStripe={() => { setLoading(true); checkStatus(true); }}
          ziinaSummary={ledgerSummary?.byProcessor?.ziina}
          stripeSummary={ledgerSummary?.byProcessor?.stripe}
        />

        <UnifiedLedger orgId={orgId} ziinaStatus={ziinaStatus} onSummary={setLedgerSummary} />

        <SubscriptionsPanel orgId={orgId} hideWhenEmpty />
      </div>

      <Dialog open={showTrustModal} onOpenChange={setShowTrustModal}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="flex items-center gap-3 text-sm font-medium">
            <Shield className="w-4 h-4 text-blue-600" />
            Connect to Stripe
          </DialogTitle>
          <p className="text-sm text-gray-600">You'll be redirected to Stripe to complete the connection.</p>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 mt-1">
            {['Payments deposited directly to you', 'Full access to your Stripe Dashboard', 'Cancel anytime'].map(item => (
              <div key={item} className="flex items-center gap-2.5 px-3 py-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                <span className="text-[13px] text-gray-600">{item}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => setShowTrustModal(false)} className="flex-1 h-9 rounded-lg border border-gray-200 text-[13px] text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
            <button onClick={handleConfirmStripe} disabled={connecting} className="flex-1 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-medium transition-colors flex items-center justify-center disabled:opacity-60">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default PaymentsTabSimple;
